import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Plus, KeyRound, ShieldCheck } from "lucide-react";

function FormField({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#50493b] subtitle block mb-1.5">
        {label}
      </Label>
      {children}
    </div>
  );
}

function SwitchRow({ label, description, testid, checked, onChange, disabled }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <div className="text-sm font-medium text-[#33424f]">{label}</div>
        <div className="text-[11px] text-[#50493b]">{description}</div>
      </div>
      <Switch
        data-testid={testid}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
      />
    </div>
  );
}

export const EmployeeFormDialog = ({
  open,
  onOpenChange,
  onNew,
  editing,
  form,
  setForm,
  error,
  onSubmit,
  canWrite,
  isAdmin,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogTrigger asChild>
      <Button
        data-testid="new-employee-btn"
        onClick={onNew}
        className={`bg-[#39678f] hover:bg-[#2c5272] text-white subtitle uppercase tracking-wider text-xs h-10 ${canWrite ? "" : "!hidden"}`}
      >
        <Plus className="w-4 h-4 mr-2" /> New employee
      </Button>
    </DialogTrigger>
    <DialogContent className="border border-[#33424f]/30 max-w-xl">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl tracking-tight text-[#33424f]">
          {editing ? "Edit employee" : "Create employee"}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Full name *">
            <Input
              data-testid="employee-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </FormField>
          <FormField label="Role">
            <Select
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v })}
            >
              <SelectTrigger data-testid="employee-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="welder">Welder</SelectItem>
                <SelectItem value="tester">Tester</SelectItem>
                <SelectItem value="both">Welder + Tester</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Email">
            <Input
              type="email"
              data-testid="employee-email-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </FormField>
          <FormField label="Phone">
            <Input
              type="tel"
              data-testid="employee-phone-input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </FormField>
        </div>

        <div className="border border-[#33424f]/15 bg-[#f5f1ec] p-4 space-y-3">
          <div className="flex items-center gap-2 text-[11px] subtitle uppercase tracking-[0.2em] text-[#39678f]">
            <KeyRound className="w-3.5 h-3.5" /> Shop-floor login
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Username">
              <Input
                data-testid="employee-username-input"
                value={form.username}
                placeholder="e.g. Ashley"
                onChange={(e) => setForm({ ...form, username: e.target.value })}
              />
            </FormField>
            <FormField label={editing ? "New PIN (6 digits, leave blank to keep)" : "PIN (6 digits)"}>
              <Input
                data-testid="employee-pin-input"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={form.pin}
                placeholder="••••••"
                onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })}
              />
            </FormField>
          </div>
          {editing?.login_enabled && (
            <div className="flex items-center gap-2 text-[11px] text-[#2d5e1f] subtitle uppercase tracking-[0.2em]">
              <ShieldCheck className="w-3.5 h-3.5" /> Login is currently enabled
            </div>
          )}
        </div>

        <div className="border border-[#33424f]/15 p-4 space-y-3">
          <div className="text-[11px] subtitle uppercase tracking-[0.2em] text-[#39678f]">
            Accessibility & theme
          </div>
          <SwitchRow
            label="High Contrast / Large Text mode"
            description="Bigger fonts, simpler buttons. Aimed at staff who aren't computer-literate. Admin-set."
            testid="employee-high-contrast-switch"
            checked={form.high_contrast}
            onChange={(v) => setForm({ ...form, high_contrast: v })}
            disabled={!isAdmin}
          />
          <SwitchRow
            label="Default to Dark Mode"
            description="Web interface only — never affects PDFs."
            testid="employee-dark-mode-switch"
            checked={form.dark_mode}
            onChange={(v) => setForm({ ...form, dark_mode: v })}
          />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={form.active}
            onCheckedChange={(v) => setForm({ ...form, active: !!v })}
            className="data-[state=checked]:bg-[#39678f] data-[state=checked]:border-[#39678f]"
          />
          Active
        </label>
        {error && (
          <div className="p-2 border border-[#b30035] bg-[#fbe4ea] text-[#7a0024] text-xs font-body">{error}</div>
        )}
        <DialogFooter>
          <Button
            data-testid="save-employee-btn"
            type="submit"
            className="bg-[#39678f] hover:bg-[#2c5272] text-white subtitle uppercase tracking-wider text-xs"
          >
            {editing ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);
